<script setup lang="ts">
import { User } from "firebase/auth";
import { Ref } from "vue";
import logoMenuOpen from "assets/graphics/icons/cd-logo-white.png";
import logoMenuClosed from "assets/graphics/icons/logo/heart-logo.svg";
import placeHolderImg from "assets/graphics/img/placeholder_contactperson.svg";
import { CareDay } from "~/types/careDay";
import { ImageUploadPayload } from "~/types/imageUpload";
import { UserFirestore } from "~/types/user";

const props = defineProps({
  isMenuOpen: {
    type: Boolean,
    required: true
  }
});

const { t } = useI18n();

const { PROFILE_IMAGE_NAME } = useConfig();

const emit = defineEmits(["menu-click", "on-mouse-enter", "on-mouse-leave"]);

const firebaseUser: Ref<User | null> = useFirebaseUser();

const userStore = useUserStore();
const userStoreRefs = storeToRefs(userStore);
const user: Ref<UserFirestore | null> = userStoreRefs.user;

const imagePath = ref<string[]>([]);
const imageUrl = ref(null);

const refreshProfileImage = async () => {
  if (user.value && user.value.id) {
    imagePath.value = [
      "profileImages",
      user.value.id,
      `${PROFILE_IMAGE_NAME}.png`
    ];
    imageUrl.value = await getImageInPathSegments(imagePath.value);
  }
};

watchEffect(async () => {
  await refreshProfileImage();
});

const userProfileImage = computed(() => imageUrl.value);

const userName = computed(() => (
  (user && user.value && user.value.name) ||
    (firebaseUser &&
      firebaseUser.value &&
      (firebaseUser.value.providerData[0].displayName ||
        firebaseUser.value.providerData[0].email))
));

const currentCareDay: Ref<CareDay | null> = useCurrentCareDay();
const careDayIdIsSet = computed(() => !!currentCareDay.value?.id);

const pathPrefix = computed(() => {
  if (!currentCareDay.value) {
    return "";
  }

  return `/careday/${currentCareDay.value.id}`;
});

const pathForRoute = (route: any) => {
  if (pathPrefix.value === "" || !currentCareDay.value) {
    return "/";
  }
  return `/careday/${currentCareDay.value.id}/${route}`;
};

const toggleMenu = () => {
  emit("menu-click");
};
const onMouseEnter = () => {
  emit("on-mouse-enter");
};
const onMouseLeave = () => {
  emit("on-mouse-leave");
};

const profileImageModalVisible = ref(false);
const profileImageItem: Ref<ImageUploadPayload | undefined> = ref(undefined);
const getProfileImageData = () => {
  if (user.value) {
    const imageUrlToDisplay = imageUrl.value || placeHolderImg;
    const uploadUri = `profileImages/${user.value.id}/${PROFILE_IMAGE_NAME}.png`;
    return new ImageUploadPayload(imageUrlToDisplay, uploadUri);
  }
};
const openProfileImageModal = () => {
  profileImageItem.value = getProfileImageData();
  profileImageModalVisible.value = true;
};
const closeProfileImageModal = () => {
  profileImageModalVisible.value = false;
  profileImageItem.value = undefined;
};
const submitProfileImageModal = async () => {
  profileImageModalVisible.value = false;
  profileImageItem.value = undefined;
  await refreshProfileImage();
  showAlertMessage("success", t("main_menu.profile_image_updated"));
};
// Banner Alerts
const {
  showAlert,
  alertType,
  alertMessage,
  alertId,
  showAlertMessage,
  resetAlert
} = useBannerAlert();
</script>

<template>
  <BannerAlerts
    :show="(showAlert as boolean)"
    :message="(alertMessage as string)"
    :alert-type="(alertType as string)"
    :alert-id="(alertId as string)"
    @reset="resetAlert"
  />
  <ModalTitleImageUpload
    v-if="profileImageModalVisible"
    :item="profileImageItem"
    :title="$t('main_menu.change_profile_image')"
    :show-delete-image="userProfileImage ? true : false"
    @close="closeProfileImageModal"
    @submit="submitProfileImageModal"
  />
  <div
    :class="[
      'navbar-wrapper',
      { 'navbar-wrapper--opened': props.isMenuOpen },
      {
        'navbar-wrapper--closed': !props.isMenuOpen,
      },
    ]"
  >
    <div class="navbar-arrow-wrapper">
      <div
        :class="['navbar', { 'flex-grow': props.isMenuOpen }]"
        @click="toggleMenu"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
      >
        <div :class="['full-links', { 'full-closed': !props.isMenuOpen }]">
          <div class="logo-wrapper">
            <nuxt-link :to="'/'" @click.stop>
              <img
                v-if="props.isMenuOpen"
                :src="logoMenuOpen"
                style="height: 26px"
              >
              <img v-else :src="logoMenuClosed" class="logo-menu-closed">
            </nuxt-link>
          </div>
          <div class="user-info">
            <div class="d-flex align-items-center border-bottom">
              <div class="user-icon-container">
                <img
                  v-if="userProfileImage"
                  :src="userProfileImage"
                  :class="[
                    'user-image',
                    { 'user-image-small': !props.isMenuOpen },
                  ]"
                  @click.stop="openProfileImageModal"
                >
                <font-awesome-icon
                  v-else
                  :icon="['fas', 'circle-user']"
                  style="color: #ced9df"
                  :class="[
                    'user-image',
                    { 'user-image-small': !props.isMenuOpen },
                  ]"
                  @click.stop="openProfileImageModal"
                />
              </div>
              <div
                v-if="props.isMenuOpen"
                :class="[
                  'user-name',
                  { 'user-name-hidden': !props.isMenuOpen },
                ]"
                @click.stop
              >
                {{ userName }}
              </div>
            </div>
          </div>
          <div
            v-show="careDayIdIsSet"
            class="navbar-links"
            :class="{ active: props.isMenuOpen }"
          >
            <NavBarLink
              :text="props.isMenuOpen ? $t('navigation.activities') : ''"
              icon="cb_activity_small.svg"
              :to="pathForRoute('activities')"
              class="nav-link"
              @click.stop
            />
            <NavBarLink
              :text="props.isMenuOpen ? $t('navigation.news') : ''"
              icon="cb_news2.svg"
              :to="pathForRoute('news')"
              class="nav-link"
              @click.stop
            />
            <NavBarLink
              :text="props.isMenuOpen ? $t('navigation.opening_times') : ''"
              icon="cb_time_small.svg"
              :to="pathForRoute('openingtimes')"
              class="nav-link"
              @click.stop
            />
            <NavBarLink
              :text="props.isMenuOpen ? $t('navigation.contact_teams') : ''"
              icon="cb_contact_small.svg"
              highlight-sub-paths
              :to="pathForRoute('contactteam')"
              class="nav-link"
              @click.stop
            />
            <NavBarLink
              :text="props.isMenuOpen ? $t('navigation.menu') : ''"
              icon="cb_food_small.svg"
              :to="pathForRoute('menu')"
              class="nav-link"
              @click.stop
            />
            <NavBarLink
              :text="props.isMenuOpen ? $t('navigation.image_galleries') : ''"
              icon="cb_pics_small.svg"
              highlight-sub-paths
              :to="pathForRoute('imagegallery')"
              class="nav-link"
              @click.stop
            />
            <NavBarLink
              :text="props.isMenuOpen ? $t('navigation.accommodation') : ''"
              icon="cb_residence_small.svg"
              highlight-sub-paths
              :to="pathForRoute('accommodation')"
              class="nav-link"
              @click.stop
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.navbar-wrapper {
  position: absolute;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 10;
}

.logo-menu-closed {
  height: 26px;
}

.user-icon-container {
  width: 26px;
  height: 26px;
}

.user-image,
.user-icon {
  width: 26px;
  height: 26px;
  border-radius: 50%;
}

.user-image-small,
.user-icon-small {
  width: 26px;
  height: 26px;
}

.user-info {
  display: flex;
  align-items: center;
  font-size: 1.2rem;
  padding: 30px;
  height: 250px;
  max-width: 368px;
  min-width: 0;
}

.user-name {
  font-size: 1.25rem;
  padding: 6px 12px 0;
  text-wrap: wrap;
  word-break: break-all;
  overflow: hidden;
  opacity: 1;
  max-width: 280px; /* Adjust as necessary */
  min-width: 280px;
  transition: opacity 0.3s ease, max-width 0.3s ease;
}

.user-name-hidden {
  opacity: 0;
  max-width: 0;
}

.border-bottom {
  padding-bottom: 20px;
  border-bottom: 1px solid #f4f6fc !important;
  width: 100%;
}

.navbar-wrapper--opened {
  width: 368px;
}

.navbar-wrapper--closed {
  width: 88px;
}

.full-closed {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.full-links {
  width: 100%;
  position: sticky;
  top: 0;
}

.left-button {
  position: absolute;
  right: 0;
  padding-top: 0px;
  box-shadow: 23px 0px 0px 0px rgba(245, 247, 252, 1);
}

.logo-wrapper {
  height: 80px;
  display: flex;
  align-items: center;
  padding: 20px 30px 20px;
}

.flex-grow {
  flex-grow: 1;
}

.background-color {
  background-color: #073946;
}

.navbar-arrow-wrapper {
  display: flex;
  height: 100%;
}

.navbar {
  color: white;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 0 20px 0 0;
  background-color: #073946;
  box-shadow: 2px 2px 6px #073946;
}

.toggle-button {
  margin-left: -1px;
}

.right-button {
  position: absolute;
  right: -14px;
  padding-top: 0px;
}

.top-202 {
  top: 202px;
}

.top-188 {
  top: 188px;
}

.navbar-links {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow: hidden;
}

.navbar-links.active {
  width: 100%;
}

.nav-link {
  font-size: 1.3rem;
  list-style: none;
  width: 100%;
}

@media screen and (max-width: 925px) {
  .nav-link {
    padding: 0;
  }
}
</style>
